import { useEffect, useState } from "react";
import { Section, Eyebrow, H2, Lead, GlassCard, Reveal } from "../components/site/Primitives";
import StatusBadge from "../components/site/StatusBadge";
import { MapPin, Bed, Bath, Maximize } from "lucide-react";
import api from "../lib/api";
import { log } from "../lib/logger";

const FILTERS = [
  { v: "all", l: "All" }, { v: "residential", l: "Residential" }, { v: "commercial", l: "Commercial" }, { v: "land", l: "Land" },
];

export default function Realty() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    const url = filter === "all" ? "/properties" : `/properties?type=${filter}`;
    api.get(url).then((r) => setItems(r.data.items)).catch((e) => log.warn("properties fetch failed", e?.message));
  }, [filter]);

  return (
    <div>
      <Section className="pt-40">
        <div className="flex items-center gap-3 flex-wrap mb-3">
          <Eyebrow>220 Realty · Listings & Investment</Eyebrow>
          <StatusBadge kind="concept" label="Sample Portfolio · Illustrative" />
        </div>
        <H2 className="max-w-4xl">Residential, commercial, and land — a planned portfolio across the corridor.</H2>
        <Lead className="mt-6">
          The listings below illustrate the planned 220 Realty portfolio thesis — build assets that compound
          community value across riverfront residential, commercial, and industrial parcels. Properties shown
          are sample concepts; no assets have been acquired and no listings are currently for sale or lease.
        </Lead>
      </Section>

      <Section className="pt-0">
        <div className="flex gap-2 mb-8">
          {FILTERS.map((f) => (
            <button
              key={f.v}
              onClick={() => setFilter(f.v)}
              data-testid={`realty-filter-${f.v}`}
              className={`px-4 py-2 text-sm border transition-colors ${
                filter === f.v ? "border-gold text-gold bg-gold/5" : "border-white/10 text-white/70 hover:border-white/30"
              }`}
            >
              {f.l}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((p, i) => (
            <Reveal key={p.id} delay={i * 0.04}>
              <GlassCard className="overflow-hidden h-full">
                <div className="aspect-[16/10] overflow-hidden relative">
                  <img src={p.image} alt={p.title} className="w-full h-full object-cover" />
                  <div className="absolute top-4 left-4 px-3 py-1 bg-black/70 text-xs uppercase tracking-[0.2em] text-gold">{p.for}</div>
                </div>
                <div className="p-6">
                  <div className="text-xs uppercase tracking-[0.2em] text-white/40 mb-2 flex items-center gap-2"><MapPin size={12} /> {p.city}</div>
                  <div className="font-display text-xl mb-3">{p.title}</div>
                  <p className="text-sm text-white/60 mb-5">{p.description}</p>
                  <div className="flex items-center gap-4 text-xs text-white/60 border-t border-white/10 pt-4">
                    {p.beds > 0 && <span className="flex items-center gap-1"><Bed size={14} /> {p.beds}</span>}
                    {p.baths > 0 && <span className="flex items-center gap-1"><Bath size={14} /> {p.baths}</span>}
                    <span className="flex items-center gap-1"><Maximize size={14} /> {p.sqft.toLocaleString()} sqft</span>
                    <span className="ml-auto text-gold font-semibold">
                      {p.price > 0 ? `$${p.price.toLocaleString()}${p.for === "rent" ? "/mo" : ""}` : "Program"}
                    </span>
                  </div>
                </div>
              </GlassCard>
            </Reveal>
          ))}
        </div>
      </Section>

      <Section className="border-t border-white/5">
        <Eyebrow>Property Management</Eyebrow>
        <H2 className="max-w-3xl">Tenants & owners — managed with precision.</H2>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
          {["Online Applications", "Rent Payment Portal", "Maintenance Tickets", "Owner Reporting", "AI Property Assistant", "24/7 Response"].map((f) => (
            <GlassCard key={f} className="p-6">
              <div className="text-xs text-gold uppercase tracking-[0.2em] mb-2">Feature</div>
              <div className="font-display text-lg">{f}</div>
            </GlassCard>
          ))}
        </div>
      </Section>
    </div>
  );
}
